/**
 * Lightweight accessible tabs — no Radix dependency.
 * Follows ARIA authoring practices 1.1 tablist pattern.
 */
import { createContext, useContext, useState } from 'react';
import { cn } from '@/lib/utils';

// ─── Context ─────────────────────────────────────────────────────────────────

interface TabsContextValue {
  active: string;
  setActive: (value: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('Tabs sub-components must be used inside <Tabs>');
  return ctx;
}

// ─── Tabs (root) ─────────────────────────────────────────────────────────────

interface TabsProps {
  defaultValue: string;
  children: React.ReactNode;
  className?: string;
}

export function Tabs({ defaultValue, children, className }: TabsProps) {
  const [active, setActive] = useState(defaultValue);
  return (
    <TabsContext.Provider value={{ active, setActive }}>
      <div className={cn('w-full', className)}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

// ─── TabsList ────────────────────────────────────────────────────────────────

interface TabsListProps {
  children: React.ReactNode;
  className?: string;
  role?: string;
}

export function TabsList({ children, className, role = 'tablist' }: TabsListProps) {
  return (
    <div
      role={role}
      className={cn(
        'inline-flex h-9 items-center justify-start rounded-lg bg-muted p-1 text-muted-foreground gap-0.5',
        className,
      )}
    >
      {children}
    </div>
  );
}

// ─── TabsTrigger ─────────────────────────────────────────────────────────────

interface TabsTriggerProps {
  value: string;
  children: React.ReactNode;
  className?: string;
  role?: string;
}

export function TabsTrigger({ value, children, className, role = 'tab' }: TabsTriggerProps) {
  const { active, setActive } = useTabsContext();
  const isActive = active === value;

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setActive(value);
    }
  }

  return (
    <button
      role={role}
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
      onClick={() => { setActive(value); }}
      onKeyDown={handleKeyDown}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium',
        'transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        isActive
          ? 'bg-background text-foreground shadow-sm'
          : 'hover:bg-background/60 hover:text-foreground',
        className,
      )}
    >
      {children}
    </button>
  );
}

// ─── TabsContent ─────────────────────────────────────────────────────────────

interface TabsContentProps {
  value: string;
  children: React.ReactNode;
  className?: string;
  role?: string;
  'aria-label'?: string;
}

export function TabsContent({
  value,
  children,
  className,
  role = 'tabpanel',
  'aria-label': ariaLabel,
}: TabsContentProps) {
  const { active } = useTabsContext();
  if (active !== value) return null;
  return (
    <div
      role={role}
      aria-label={ariaLabel}
      tabIndex={0}
      className={cn(
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded',
        className,
      )}
    >
      {children}
    </div>
  );
}
